import { useState, useEffect } from 'react'

interface CountryPricing {
  totalPrice: string
  todayPrice: string
  countryName: string
  countryCode: string
  loading: boolean
}

const PRICING_MAP: Record<
  string,
  { totalPrice: string; todayPrice: string; countryName: string }
> = {
  UY: { totalPrice: '$6.333', todayPrice: '$633', countryName: 'Uruguay' },
  MX: { totalPrice: '$3,238.72', todayPrice: '$323.87', countryName: 'México' },
  CO: {
    totalPrice: '$595.160',
    todayPrice: '$59.516',
    countryName: 'Colombia',
  },
  AR: {
    totalPrice: '$242.027',
    todayPrice: '$24.202',
    countryName: 'Argentina',
  },
  PE: { totalPrice: 'S/ 554.00', todayPrice: 'S/ 55.40', countryName: 'Perú' },
  CL: { totalPrice: '$167.570', todayPrice: '$16.757', countryName: 'Chile' },
  PY: {
    totalPrice: 'Gs. 1.018.362',
    todayPrice: 'Gs. 101.836',
    countryName: 'Paraguay',
  },
  PA: {
    totalPrice: 'B/. 159.00',
    todayPrice: 'B/. 15.90',
    countryName: 'Panamá',
  },
  CR: {
    totalPrice: '₡75.334',
    todayPrice: '₡7.533',
    countryName: 'Costa Rica',
  },
  PR: {
    totalPrice: '$149.99',
    todayPrice: '$14.99',
    countryName: 'Puerto Rico',
  },
  EC: { totalPrice: '$149.99', todayPrice: '$14.99', countryName: 'Ecuador' },
  BO: {
    totalPrice: 'Bs 1.099,00',
    todayPrice: 'Bs 109,90',
    countryName: 'Bolivia',
  },
  SV: {
    totalPrice: '$149.99',
    todayPrice: '$14.99',
    countryName: 'El Salvador',
  },
  GT: {
    totalPrice: 'Q 1,214.00',
    todayPrice: 'Q 121.40',
    countryName: 'Guatemala',
  },
  ES: { totalPrice: '142 €', todayPrice: '14,25 €', countryName: 'España' },
  US: {
    totalPrice: 'USD 149,99',
    todayPrice: 'USD 14,99',
    countryName: 'Estados Unidos',
  },
}

const DEFAULT_PRICING = {
  totalPrice: 'USD 149,99',
  todayPrice: 'USD 14,99',
  countryName: '',
  countryCode: 'US',
}

export function useCurrencyDetection(): CountryPricing {
  const [pricing, setPricing] = useState<CountryPricing>({
    ...DEFAULT_PRICING,
    loading: true,
  })

  useEffect(() => {
    let cancelled = false

    async function detectCountry() {
      try {
        const response = await fetch('https://ipapi.co/json/')
        if (!response.ok) throw new Error('IP detection failed')
        const data = await response.json()
        const countryCode: string = data.country_code || 'US'

        if (cancelled) return

        const countryPricing = PRICING_MAP[countryCode]

        if (countryPricing) {
          setPricing({
            totalPrice: countryPricing.totalPrice,
            todayPrice: countryPricing.todayPrice,
            countryName: countryPricing.countryName,
            countryCode,
            loading: false,
          })
        } else {
          setPricing({
            ...DEFAULT_PRICING,
            countryCode,
            loading: false,
          })
        }
      } catch {
        if (!cancelled) {
          setPricing({
            ...DEFAULT_PRICING,
            loading: false,
          })
        }
      }
    }

    detectCountry()

    return () => {
      cancelled = true
    }
  }, [])

  return pricing
}
